
import React, { useMemo } from "react";
import dayjs from "dayjs";
import "dayjs/locale/it";
import { DocumentTextIcon, UserMinusIcon, DocumentCheckIcon } from "@heroicons/react/24/outline";

dayjs.locale("it");

export default function MiniDeadlinesList({ docs = [], contracts = [], signedDocs = [], month, onItemClick }) {
  const currentMonth = useMemo(
    () => (month ? dayjs(month).startOf("month") : dayjs().startOf("month")),
    [month]
  );

  const items = useMemo(() => {
    const out = [];

    (docs || []).forEach((d) => {
      const due = d.due_date || d.expiry_date || d.date;
      if (!due) return;

      const dd = dayjs(due);
      if (!dd.isValid() || dd.format("YYYY-MM") !== currentMonth.format("YYYY-MM")) return;

      out.push({
        kind: "document",
        entity: d,
        date: dd,
        label: d.name || d.nome || d.title || `Documento #${d.id ?? ""}`.trim(),
      });
    });

    (signedDocs || []).forEach((d) => {
      const due = d.due_date;
      if (!due) return;

      const dd = dayjs(due);
      if (!dd.isValid() || dd.format("YYYY-MM") !== currentMonth.format("YYYY-MM")) return;

      out.push({
        kind: "signed-document",
        entity: d,
        date: dd,
        label: d.name || `Documento con firma #${d.id ?? ""}`.trim(),
      });
    });

    (contracts || []).forEach((c) => {
      const due = c.due_date || c.end_date;
      if (!due) return;

      const dd = dayjs(due);
      if (!dd.isValid() || dd.format("YYYY-MM") !== currentMonth.format("YYYY-MM")) return;

      const emp = c.employee || {};
      const label =
        [emp.last_name, emp.first_name].filter(Boolean).join(" ") ||
        emp.cf ||
        `Contratto #${c.id ?? ""}`.trim();

      out.push({
        kind: "contract",
        entity: c,
        date: dd,
        label,
      });
    });

    return out
      .sort((a, b) => a.date.valueOf() - b.date.valueOf() || a.kind.localeCompare(b.kind))
      .slice(0, 50);
  }, [docs, contracts, signedDocs, currentMonth]);

  if (items.length === 0) {
    return (
      <div className="rounded-md border border-gray-200 bg-white" style={{ marginTop: "-25px" }}>
        <h3 className="text-xs font-semibold text-indigo-800 py-1 px-3 bg-indigo-200 uppercase justify-center flex">
          Elenco scadenze
        </h3>
        <div className="max-h-32 min-h-12 h-12 flex align-center justify-center overflow-y-auto p-3">
          <p className="italic text-sm text-gray-500">Nessuna scadenza presente</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-md border border-gray-200 bg-white"
      style={{ marginTop: "-25px", scrollbarWidth: "thin", scrollbarColor: "#c7d2fe transparent" }}
    >
      <h3 className="text-xs font-semibold text-indigo-800 py-1 px-3 bg-indigo-200 uppercase justify-center flex">
        Elenco scadenze
      </h3>

      <div className="max-h-32 min-h-32 h-32 overflow-y-auto p-3">
        <ul className="space-y-1.5 text-xs text-gray-900">
          {items.map((item, idx) => {
            const isDocument = item.kind === "document";
            const isSignedDocument = item.kind === "signed-document";

            const Icon = isDocument
              ? DocumentTextIcon
              : isSignedDocument
                ? DocumentCheckIcon
                : UserMinusIcon;

            const iconClass = isDocument
              ? "bg-indigo-50 text-indigo-600 border-indigo-300"
              : isSignedDocument
                ? "bg-green-50 text-green-600 border-green-400"
                : "bg-amber-50 text-amber-600 border-amber-400";

            const dateStr = item.date.format("DD MMMM");

            return (
              <li
                key={`${item.kind}-${item.entity?.id ?? idx}`}
                className="flex items-center justify-between border-b last:border-0 border-gray-100 py-1 px-2 cursor-pointer hover:bg-indigo-50"
                onClick={() => onItemClick?.({ kind: item.kind, entity: item.entity })}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${iconClass}`}>
                    <Icon className="h-3 w-3" />
                  </span>
                  <div className="flex flex-col min-w-0">
                    <span className="font-medium truncate">{item.label}</span>
                  </div>
                </div>
                <span className="text-[11px] uppercase text-gray-900 flex-shrink-0 ml-2">
                  {dateStr}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}